import { createTheme as createMuiTheme, Theme, ThemeOptions, PaletteColorOptions } from '@mui/material/styles';
import RadioButtonChecked from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { createTheme as createNCRTheme } from '@ncr-design-system/shared-frontend';
import { commonThemeOptions, commonPaletteOptions, dsCommonThemeOptions } from './commonThemeOptions';

declare module '@mui/material/styles' {
  interface PaletteColor {
    subtle: string;
    subtleHover: string;
    subtleContrast: string;
    contrast: string;
    darkAccent?: string;
    subtleAccent?: string;
    lightAccent?: string;
  }

  interface SimplePaletteColorOptions {
    subtle?: string;
    subtleHover?: string;
    subtleContrast?: string;
    contrast?: string;
    darkAccent?: string;
  }

  interface Palette {
    graphColors: string[];
    surfaceBorder: string;
    interactiveBorder: string;
    category1: PaletteColor;
    category2: PaletteColor;
    category3: PaletteColor;
    category4: PaletteColor;
    category5: PaletteColor;
    category6: PaletteColor;
    category7: PaletteColor;
    category8: PaletteColor;
    report: PaletteColor;
  }

  interface PaletteOptions {
    graphColors?: string[];
    surfaceBorder?: string;
    interactiveBorder?: string;
    category1?: PaletteColorOptions;
    category2?: PaletteColorOptions;
    category3?: PaletteColorOptions;
    category4?: PaletteColorOptions;
    category5?: PaletteColorOptions;
    category6?: PaletteColorOptions;
    category7?: PaletteColorOptions;
    category8?: PaletteColorOptions;
    report?: PaletteColorOptions;
  }

  interface TypeAction {
    selectedHover: string;
    selectedFocus: string;
  }

  interface TypographyVariants {
    display1: React.CSSProperties;
    display2: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    display1?: React.CSSProperties;
    display2?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true;
    display2: true;
  }
}

/** RESTAURANTS UI KIT THEME */
const reportPalette = {
  main: '#303030', // neutral text / buttons
  light: '#828282',
  subtle: '#ECE1FE', // chip background
  subtleHover: '#e0cdfe', // chip background hover
  darkAccent: '#5F249F', // chip text / icons
  subtleAccent: '#D8ECFD',
  lightAccent: '#70B3FA', // focus outline
};

const lightThemeOptions: ThemeOptions = {
  ...commonThemeOptions,
  palette: {
    ...commonPaletteOptions,
    mode: 'light',
    primary: {
      main: '#303030',
      dark: '#1A1A1A',
      light: '#828282',
      contrastText: '#FFFFFF',
      subtle: '#E0E0E0',
      subtleHover: '#CFCFCF',
      subtleContrast: '#000000',
    },
    secondary: {
      main: '#5F249F',
      dark: '#42196F',
      light: '#B280FF',
      contrastText: '#FFFFFF',
      subtle: '#ECE1FE',
      subtleHover: '#E0CDFE',
      subtleContrast: '#2F124F',
    },
    success: {
      main: '#24802D',
      dark: '#064E05',
      light: '#3CC84A',
      contrastText: '#FFFFFF',
      subtle: '#DAEED8',
      subtleHover: '#C3E3C0',
      subtleContrast: '#08400E',
    },
    error: {
      main: '#DD1708',
      dark: '#890802',
      light: '#FB9189',
      contrastText: '#FFFFFF',
      subtle: '#FDE3E3',
      subtleHover: '#FBCCCB',
      subtleContrast: '#710B01',
    },
    warning: {
      main: '#AB5A05',
      dark: '#653700',
      light: '#F99934',
      contrastText: '#FFFFFF',
      subtle: '#FCE3CA',
      subtleHover: '#FAD4AD',
      subtleContrast: '#552C07',
    },
    info: {
      main: '#086DD9',
      dark: '#134085',
      light: '#70B3FA',
      contrastText: '#FFFFFF',
      subtle: '#D8ECFD',
      subtleHover: '#BBDEFB',
      subtleContrast: '#053570',
    },
    text: {
      primary: '#212121',
      secondary: '#656565',
      disabled: '#9E9E9E',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    action: {
      active: 'rgba(0,0,0,0.54)',
      hover: 'rgba(0,0,0,0.04)',
      hoverOpacity: 0.04,
      selected: 'rgba(0,0,0,0.08)',
      selectedOpacity: 0.08,
      disabled: 'rgba(0,0,0,0.26)',
      disabledBackground: 'rgba(0,0,0,0.12)',
      disabledOpacity: 0.38,
      focus: 'rgba(0,0,0,0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
      selectedFocus: 'rgba(0,0,0,0.20)',
      selectedHover: 'rgba(0,0,0,0.12)',
    },
    divider: '#E0E0E0',
    category1: {
      main: '#2B69DD',
      contrast: '#FFFFFF',
      subtle: '#E2EBFD',
      subtleHover: '#B7CEFB',
      subtleContrast: '#07307D',
    },
    category2: {
      main: '#139A7F',
      contrast: '#FFFFFF',
      subtle: '#E4F6F4',
      subtleHover: '#94E0D7',
      subtleContrast: '#07453E',
    },
    category3: {
      main: '#A6305C',
      contrast: '#FFFFFF',
      subtle: '#F8E2EC',
      subtleHover: '#F1C6D8',
      subtleContrast: '#621E3A',
    },
    category4: {
      main: '#B9840E',
      contrast: '#FFFFFF',
      subtle: '#FCF4E4',
      subtleHover: '#EFCD80',
      subtleContrast: '#583C0E',
    },
    category5: {
      main: '#C461C6',
      contrast: '#FFFFFF',
      subtle: '#F7E8F7',
      subtleHover: '#EBC5EC',
      subtleContrast: '#69156A',
    },
    category6: {
      main: '#213873',
      contrast: '#FFFFFF',
      subtle: '#E7ECF8',
      subtleHover: '#C4CFEE',
      subtleContrast: '#1E3267',
    },
    category7: {
      main: '#D85337',
      contrast: '#FFFFFF',
      subtle: '#F9E5E1',
      subtleHover: '#F3C8BF',
      subtleContrast: '#622113',
    },
    category8: {
      main: '#5F249F',
      contrast: '#FFFFFF',
      subtle: '#F0E6F9',
      subtleHover: '#DDC9F3',
      subtleContrast: '#48157F',
    },
    report: reportPalette,
    graphColors: ['#203A7E', '#4A5AFF', '#069887', '#D07420', '#B03569', '#C461C6', '#D85337', '#5F249F'],
    surfaceBorder: '#E0E0E0',
    interactiveBorder: '#919191',
  },
};

const darkThemeOptions: ThemeOptions = {
  ...commonThemeOptions,
  palette: {
    ...commonPaletteOptions,
    mode: 'dark',
    primary: {
      main: '#EEEEEE',
      dark: '#FFFFFF',
      light: '#757575',
      contrastText: '#292929',
      subtle: '#454545',
      subtleHover: '#575757',
      subtleContrast: '#FFFFFF',
    },
    secondary: {
      main: '#B280FF',
      dark: '#E3D1FF',
      light: '#5F259F',
      contrastText: '#292929',
      subtle: '#5C2499',
      subtleHover: '#521F89',
      subtleContrast: '#F3EBFF',
    },
    error: {
      main: '#FF6459',
      dark: '#FFC9C7',
      light: '#BA1408',
      contrastText: '#292929',
      subtle: '#6F2121',
      subtleHover: '#7E2525',
      subtleContrast: '#FFE3E1',
    },
    warning: {
      main: '#DC8126',
      dark: '#FECE8F',
      light: '#874D12',
      contrastText: '#292929',
      subtle: '#563514',
      subtleHover: '#623D18',
      subtleContrast: '#FFE6BF',
    },
    info: {
      main: '#379DEE',
      dark: '#B2DBFC',
      light: '#085DA0',
      contrastText: '#292929',
      subtle: '#043D72',
      subtleHover: '#0B467A',
      subtleContrast: '#CFEEFF',
    },
    success: {
      main: '#59A94F',
      dark: '#A3E796',
      light: '#2E6827',
      contrastText: '#292929',
      subtle: '#164612',
      subtleHover: '#1C4F17',
      subtleContrast: '#C4F7B5',
    },
    text: {
      primary: '#F2F2F2',
      secondary: '#A3A4A6',
      disabled: '#6B696D',
    },
    background: {
      default: '#171717',
      paper: '#1F1F1F',
    },
    action: {
      active: 'rgba(255,255,255,0.64)',
      hover: 'rgba(255,255,255,0.08)',
      hoverOpacity: 0.08,
      selected: 'rgba(255,255,255,0.16)',
      selectedOpacity: 0.16,
      disabled: 'rgba(255,255,255,0.30)',
      disabledBackground: 'rgba(255,255,255,0.12)',
      disabledOpacity: 0.38,
      focus: 'rgba(255,255,255,0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.24,
      selectedFocus: 'rgba(255,255,255,0.28)',
      selectedHover: 'rgba(255,255,255,0.24)',
    },
    divider: '#3C3C3C',
    tonalOffset: 0.5,
    contrastThreshold: 4.5,
    category1: {
      main: '#A2BDF0',
      contrast: '#000000',
      subtle: '#14387B',
      subtleHover: '#17408D',
      subtleContrast: '#E2E9FE',
    },
    category2: {
      main: '#07C5A3',
      contrast: '#000000',
      subtle: '#024539',
      subtleHover: '#034F41',
      subtleContrast: '#98FBE9',
    },
    category3: {
      main: '#DC4C88',
      contrast: '#000000',
      subtle: '#621E3A',
      subtleHover: '#7E264A',
      subtleContrast: '#F9D8E5',
    },
    category4: {
      main: '#FFB747',
      contrast: '#000000',
      subtle: '#4D3B00',
      subtleHover: '#5C4600',
      subtleContrast: '#FFE8B8',
    },
    category5: {
      main: '#B371CC',
      contrast: '#000000',
      subtle: '#572669',
      subtleHover: '#662C7C',
      subtleContrast: '#F1E5F6',
    },
    category6: {
      main: '#577CDB',
      contrast: '#000000',
      subtle: '#1F346B',
      subtleHover: '#274287',
      subtleContrast: '#D7E0F4',
    },
    category7: {
      main: '#E17B65',
      contrast: '#000000',
      subtle: '#6B2415',
      subtleHover: '#7C2A18',
      subtleContrast: '#F9E5E1',
    },
    category8: {
      main: '#9E63DE',
      contrast: '#000000',
      subtle: '#4B1C7D',
      subtleHover: '#5F249E',
      subtleContrast: '#E7DBF6',
    },
    report: reportPalette,
    graphColors: ['#5283E5', '#8A9BFF', '#07C5A3', '#FFB747', '#DC4C88', '#C589DC', '#E17B65', '#9E63DE'],
    surfaceBorder: '#3C3C3C',
    interactiveBorder: '#727273',
  },
};

/** DESIGN SYSTEM THEME */
const dsLightTheme: ThemeOptions = {
  ...dsCommonThemeOptions,
  palette: {
    mode: 'light',
    error: {
      main: '#DD1708',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFF',
    },
    category8: {
      main: '#5F249F',
      dark: '#42196F',
      light: '#B280FF',
      contrastText: '#FFFFFF',
      subtle: '#ECE1FE',
      subtleHover: '#E0CDFE',
      subtleContrast: '#2F124F',
    },
    graphColors: ['#203A7E', '#4A5AFF', '#069887', '#D07420', '#B03569', '#C461C6', '#D85337', '#5F249F'],
    report: reportPalette,
  },
};

const dsDarkTheme: ThemeOptions = {
  ...dsCommonThemeOptions,
  palette: {
    mode: 'dark',
    error: {
      main: '#FF6459',
    },
    background: {
      default: '#303030',
      paper: '#292929',
    },
    category8: {
      main: '#B280FF',
      dark: '#E3D1FF',
      light: '#5F259F',
      contrastText: '#292929',
      subtle: '#5C2499',
      subtleHover: '#521F89',
      subtleContrast: '#F3EBFF',
    },
    graphColors: ['#5283E5', '#8A9BFF', '#07C5A3', '#FFB747', '#DC4C88', '#C589DC', '#E17B65', '#9E63DE'],
    report: reportPalette,
  },
};

function createTheme(darkMode: boolean, useDsTheme: boolean) {
  let theme: Theme;

  if (useDsTheme) {
    theme = createNCRTheme(darkMode ? dsDarkTheme : dsLightTheme);

    const muiDefault = createMuiTheme();
    // For some reason the design system theme doesn't have this function which the UserProfileSettings component needs
    theme.applyStyles = muiDefault.applyStyles;
    // The typography styling was breaking the UserProfileSettings dialog header pretty badly, this rule
    // only impacts the report static value cards, and it's not significant. Hopefully this is just temporary.
    theme.typography.h4 = { ...muiDefault.typography.h4, fontWeight: 700 };

    if (theme.components && theme.components.MuiRadio && theme.components.MuiRadio.defaultProps) {
      theme.components.MuiRadio.defaultProps = {
        icon: <RadioButtonUnchecked />,
        checkedIcon: <RadioButtonChecked color='primary' />,
      };
    }

    if (theme.components && theme.components.MuiIconButton && theme.components.MuiIconButton.styleOverrides) {
      theme.components.MuiIconButton.styleOverrides = {
        root: () => ({
          '&:focus': {
            // Design system removes background on focus for icon buttons, but it is needed for accessibility.
            background: theme.palette.action.focus,
          },
        }),
      };
    }

    if (theme.components && theme.components.MuiCheckbox && theme.components.MuiCheckbox.defaultProps) {
      theme.components.MuiCheckbox.defaultProps = {
        sx: { color: theme.palette.grey[500] },
      };
    }
  } else {
    theme = createMuiTheme(darkMode ? darkThemeOptions : lightThemeOptions);
  }

  return theme;
}

export default createTheme;
